import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, MessageSquare, Clock, CheckCircle2, 
  ArrowRight, Phone, Mail, MapPin, Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const consultationTypes = [
  {
    title: 'Discovery Call',
    duration: '15 minutes',
    description: 'Quick introduction to discuss your needs and see if we\'re a good fit.',
    icon: Phone
  },
  {
    title: 'Strategy Session',
    duration: '30 minutes',
    description: 'Deep dive into your project requirements and get preliminary recommendations.',
    icon: MessageSquare
  },
  {
    title: 'Technical Consultation',
    duration: '45 minutes',
    description: 'Detailed technical discussion for complex projects requiring expert guidance.',
    icon: Calendar
  }
];

const contactInfo = [
  { icon: Phone, label: 'Phone', value: '(702) 727-1773', href: 'tel:+17027271773' },
  { icon: Mail, label: 'Email', value: 'info@fearlessit.io', href: 'mailto:info@fearlessit.io' },
  { icon: MapPin, label: 'Location', value: 'Las Vegas, NV (Virtually Everywhere)', href: null },
];

const consultationSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Free Consultation - Fearless IT",
  "description": "Book a free consultation with Fearless IT to discuss your technology needs. Discovery calls, strategy sessions, and technical consultations available.",
  "provider": {
    "@type": "LocalBusiness",
    "name": "Fearless IT",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Las Vegas",
      "addressRegion": "NV",
      "addressCountry": "US"
    },
    "telephone": "(702) 727-1773"
  }
};

const ConsultationPage = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    company: '',
    consultation_type: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          message: `[Consultation Request: ${formData.consultation_type || 'Not specified'}]\n\n${formData.message}`
        }),
      });
      
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ first_name: '', last_name: '', email: '', company: '', consultation_type: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Consultation form submission failed:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="consultation-page">
      <SEO 
        title="Book a Consultation - Let's Discuss Your Project"
        description="Schedule a free consultation with Fearless IT. Discuss your technology needs, get expert guidance, and explore how we can help your business grow."
        keywords="book consultation, free consultation, technology consultation, Las Vegas IT services, project consultation, business technology advice"
        url="/resources/consultation"
        structuredData={consultationSchema}
        isLocalPage={true}
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full mb-6">
            <Calendar className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">Book a Call</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-carbon-black mb-4" data-testid="consultation-title">
            Book a Consultation
          </h1>
          <p className="text-xl sm:text-2xl font-semibold mb-6" style={{ background: 'linear-gradient(to right, #0066FF, #5B3DF5)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Let's Talk About Your Project
          </p>
          <p className="text-lg sm:text-xl text-slate-gray max-w-2xl mx-auto leading-relaxed">
            Schedule a free consultation to discuss your technology needs. No pressure, no obligations - just a conversation about how we can help.
          </p>
        </div>
      </section>

      {/* Consultation Types */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-3 gap-6">
            {consultationTypes.map((type, idx) => {
              const IconComponent = type.icon;
              return (
                <div key={idx} className="text-center p-6 bg-gray-50 rounded-xl">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-bold text-carbon-black mb-1">{type.title}</h3>
                  <div className="flex items-center justify-center gap-1 text-sm text-blue-600 mb-2">
                    <Clock className="h-3 w-3" />
                    {type.duration}
                  </div>
                  <p className="text-sm text-slate-gray">{type.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-6">
                Request Your Free Consultation
              </h2>
              
              {submitStatus === 'success' ? (
                <div className="text-center py-12 px-6 bg-green-50 rounded-2xl border border-green-100">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-carbon-black mb-3">Request Received!</h3>
                  <p className="text-slate-gray mb-6">
                    Thank you for reaching out. We'll review your request and get back to you within one business day to schedule your consultation.
                  </p>
                  <Button 
                    onClick={() => setSubmitStatus(null)}
                    variant="outline"
                    className="border-gray-300"
                  >
                    Submit Another Request
                  </Button>
                </div>
              ) : submitStatus === 'error' ? (
                <div className="text-center py-12 px-6 bg-red-50 rounded-2xl border border-red-100">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-6">
                    <Send className="h-8 w-8 text-red-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-carbon-black mb-3">Something Went Wrong</h3>
                  <p className="text-slate-gray mb-6">
                    We couldn't send your request. Please try again or contact us directly.
                  </p>
                  <Button 
                    onClick={() => setSubmitStatus(null)}
                    variant="outline"
                    className="border-gray-300"
                  >
                    Try Again
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First Name *</Label>
                      <Input
                        id="first_name"
                        name="first_name"
                        type="text"
                        required
                        value={formData.first_name}
                        onChange={handleInputChange}
                        placeholder="John"
                        className="h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name *</Label>
                      <Input
                        id="last_name"
                        name="last_name"
                        type="text"
                        required
                        value={formData.last_name}
                        onChange={handleInputChange}
                        placeholder="Smith"
                        className="h-12"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="john@company.com"
                        className="h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">Company Name</Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Your Company"
                        className="h-12"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="consultation_type">Consultation Type</Label>
                    <select
                      id="consultation_type"
                      name="consultation_type"
                      value={formData.consultation_type}
                      onChange={handleInputChange}
                      className="w-full h-12 px-3 border border-gray-200 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Select a consultation type</option>
                      <option value="Discovery Call (15 min)">Discovery Call (15 min)</option>
                      <option value="Strategy Session (30 min)">Strategy Session (30 min)</option>
                      <option value="Technical Consultation (45 min)">Technical Consultation (45 min)</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Tell us about your project *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Briefly describe what you're looking to accomplish..."
                      className="min-h-[120px] resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full gradient-fearless text-white py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50"
                  >
                    {isSubmitting ? 'Sending...' : 'Request Consultation'}
                    <Send className="ml-2 h-5 w-5" />
                  </Button>
                </form>
              )}
            </div>
            
            {/* Contact Info */}
            <div>
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100 mb-8">
                <h3 className="text-xl font-bold text-carbon-black mb-6">Contact Information</h3>
                <div className="space-y-4">
                  {contactInfo.map((info, idx) => {
                    const IconComponent = info.icon;
                    return (
                      <div key={idx} className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                          <IconComponent className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-xs text-slate-gray uppercase tracking-wider">{info.label}</div>
                          {info.href ? (
                            <a href={info.href} className="font-medium text-carbon-black hover:text-blue-600 transition-colors">
                              {info.value}
                            </a>
                          ) : (
                            <div className="font-medium text-carbon-black">{info.value}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-lg">
                <h3 className="text-xl font-bold text-carbon-black mb-4">What to Expect</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-gray">We'll respond within 1 business day</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-gray">No pressure, no sales scripts</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-gray">Expert guidance from senior team members</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-gray">Clear, actionable recommendations</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Alternative CTA */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            Not Ready to Talk Yet?
          </h2>
          <p className="text-lg text-slate-gray mb-8 max-w-2xl mx-auto">
            Get a free, automated assessment of your website and digital presence with our Growth Audit tool.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/growth-audit">
              <Button className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all w-full sm:w-auto">
                Free Growth Audit
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/resources/guides">
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-blue-400 text-blue-600 hover:bg-blue-50 transition-all w-full sm:w-auto">
                Browse Resources
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default ConsultationPage;
